import { yupResolver } from "@hookform/resolvers/yup";
import _ from "lodash";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { ErrorModal, SuccessModal } from "@components";

import { CommentType } from "@components";
import {
  BasicInfo,
  Comments,
  Letter,
  SupportingDocs,
  TransferHeader,
} from "./components";

import { useTransfer } from "@hooks/HRM/transfer/useTransfer";
import { useBaseData } from "@hooks/useBaseData";
import { useUser } from "@hooks/useUser";

import {
  convertMultipleDocumentData,
  revertMultipleDocumentData,
} from "@utils/documentDataConvert";
import { GetEmployeeDetails } from "@utils/getEmployeeDetails";

import { forwardTransferFlow } from "@api/transfer";

import { BASE_DATA } from "@constants/base-data";
import { ROUTES } from "@constants/routes";
import { STATUS, TRANSFER_STATUS } from "@constants/status.hrm";

import { TransferDetailsType } from "@type/HRM/transfer";

import {
  TRANSFER,
  transferDetailsValidation,
} from "@validations/HRM/transfer/transferValidation";

const {
  HRM: { TRANSFERS },
} = ROUTES;
const {
  INITIATED,
  SENT_FOR_DOCUMENTS_UPLOAD,
  COMPLETED,
  UPLOADED_JOINING_LETTER,
} = TRANSFER_STATUS;
const { SUCCESS } = STATUS;
const {
  ASSET_DOCS,
  TRANSFER_DOCS,
  JOINING_LETTER_DOCS,
  NOC_DOCS,
  SUPPORTING_DOCS,
} = TRANSFER;

export default function TransferDetails() {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [showActionButton, setShowActionButton] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { id } = useParams();
  const navigate = useNavigate();

  const { user } = useUser();
  const { realm_roles: roles, employeeMasterId } = user || {};
  const { data: grades } = useBaseData(BASE_DATA.GRADES);

  const transfer = useTransfer(Number(id));
  const { employeeName, employeePosition, employeeImage } = GetEmployeeDetails(
    transfer?.employee,
  );

  const canModifyOwnTransfer =
    roles.includes("f_per_own_transfer") &&
    Number(employeeMasterId) === Number(transfer?.employee?.id);
  const canModifyAllTransfer = roles.includes("f_per_all_transfer");
  const permissions = canModifyAllTransfer || canModifyOwnTransfer;

  const isCompleted = transfer?.status?.toUpperCase() === COMPLETED;
  const isInitiated = transfer?.status?.toUpperCase() === INITIATED;
  const isSentForApproval =
    transfer?.status?.toUpperCase() === SENT_FOR_DOCUMENTS_UPLOAD;
  const isUploadedJoiningLetter =
    transfer?.status?.toUpperCase() === UPLOADED_JOINING_LETTER;

  const methods = useForm<TransferDetailsType>({
    resolver: yupResolver(transferDetailsValidation),
    values: {
      ...revertMultipleDocumentData(transfer, [
        ASSET_DOCS,
        TRANSFER_DOCS,
        JOINING_LETTER_DOCS,
        NOC_DOCS,
        SUPPORTING_DOCS,
      ]),
      unitId: transfer?.employee?.unitId,
      positionId: transfer?.newPositionId,
      gradeId: transfer?.newGradeId,
      basicSalary: transfer?.basicSalary ? transfer.basicSalary : null,

      payScaleMin: {
        ..._.find(grades, (_grade) => {
          return _grade.id === transfer?.newGradeId;
        }),
      }.salaryScaleFrom,

      payScaleMax: {
        ..._.find(grades, (_grade) => {
          return _grade.id === transfer?.newGradeId;
        }),
      }.salaryScaleTo,
    },
  });

  const { handleSubmit } = methods;

  const onSubmit: SubmitHandler<TransferDetailsType> = (
    data: TransferDetailsType,
  ) => {
    setIsLoading(true);
    if (!data?.basicSalary) {
      data.basicSalary = 0;
    }

    forwardTransferFlow(
      Number(id),
      convertMultipleDocumentData(data, [
        ASSET_DOCS,
        TRANSFER_DOCS,
        JOINING_LETTER_DOCS,
        NOC_DOCS,
        SUPPORTING_DOCS,
      ]),
      (response) => {
        if (response.status.toUpperCase() === SUCCESS) {
          setIsSuccessModalOpen(true);
        } else {
          setIsErrorModalOpen(true);
        }
        setIsLoading(false);
      },
    );
  };

  const closeSuccessModal = () => {
    setIsSuccessModalOpen(false);
    navigate(TRANSFERS);
  };

  const closeErrorModal = () => setIsErrorModalOpen(false);

  useEffect(() => {
    if (isSentForApproval || isUploadedJoiningLetter) {
      setShowActionButton(canModifyAllTransfer);
    } else setShowActionButton(permissions);
    setIsDisabled(!isInitiated || !permissions);
  }, [id, transfer, permissions, isInitiated]);

  return (
    <>
      {/* AFTER EFFECTS MODAL */}
      <SuccessModal
        isModalOpen={isSuccessModalOpen}
        action={closeSuccessModal}
      />
      <ErrorModal isModalOpen={isErrorModalOpen} closeModal={closeErrorModal} />
      {/* AFTER EFFECTS MODAL */}
      <FormProvider {...methods}>
        <form className="container" onSubmit={handleSubmit(onSubmit)}>
          <TransferHeader
            data={{ employeeName, employeePosition, employeeImage }}
            showActionButton={!isCompleted && showActionButton}
            isUploadedJoiningLetter={isUploadedJoiningLetter}
            isLoading={isLoading}
          />
          <div className="p-10">
            <BasicInfo isDisabled={isDisabled} transfer={transfer} />
            <Letter isDisabled={isDisabled} />
            <SupportingDocs
              status={transfer?.status}
              isDisabled={isDisabled}
              canModifyAllTransfer={canModifyAllTransfer}
              canModifyOwnTransfer={showActionButton}
            />
            <Comments
              comments={transfer ? (transfer.comments as CommentType[]) : []}
              isCompleted={isCompleted || !showActionButton}
            />
          </div>
        </form>
      </FormProvider>
    </>
  );
}
